/**
 * PopoverMenu Hook
 *
 * Positions a popover menu relative to its trigger button when opened.
 * Uses the HTML Popover API's toggle event to detect open/close.
 *
 * Browser Support:
 * - Chrome 114+, Safari 17+, Firefox 125+
 * - Falls back gracefully on unsupported browsers (menu won't open via popover)
 */

// Layout constants for menu positioning
const MENU_GAP = 8 // Gap between button and menu
const VIEWPORT_PADDING = 8 // Minimum distance from viewport edges
const SIDE_POSITION_OFFSET = -2 // Vertical offset for left/right positioned menus
const EDGE_ALIGNMENT_OFFSET = 8 // Horizontal offset for edge-aligned menus

const PopoverMenuHook = {
  mounted() {
    // Feature detection: check if Popover API is supported
    if (!this.isPopoverSupported()) {
      console.warn(
        "PopoverMenu: Popover API not supported in this browser. Menu may not function correctly.",
      )
      return
    }

    this.el.addEventListener("toggle", (event) => {
      if (event.newState !== "open") return

      // Find the trigger button
      const button = document.querySelector(`[popovertarget="${this.el.id}"]`)
      if (!button) return

      this.positionMenu(button)
    })
  },

  isPopoverSupported() {
    return (
      typeof HTMLElement !== "undefined" &&
      typeof HTMLElement.prototype.showPopover === "function"
    )
  },

  positionMenu(button) {
    const position = this.el.dataset.position || "below_right"
    const buttonRect = button.getBoundingClientRect()
    const menuRect = this.el.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let top, left

    switch (position) {
      case "right":
        top = buttonRect.top + SIDE_POSITION_OFFSET
        left = buttonRect.right + MENU_GAP
        break
      case "left":
        top = buttonRect.top + SIDE_POSITION_OFFSET
        left = buttonRect.left - menuRect.width - MENU_GAP
        break
      case "below":
        top = buttonRect.bottom + MENU_GAP
        left = buttonRect.left + buttonRect.width / 2 - menuRect.width / 2
        break
      case "below_left":
        top = buttonRect.bottom + MENU_GAP
        left = buttonRect.left - EDGE_ALIGNMENT_OFFSET
        break
      case "below_right":
      default:
        top = buttonRect.bottom + MENU_GAP
        left = buttonRect.right - menuRect.width + EDGE_ALIGNMENT_OFFSET
        break
    }

    // Keep menu within viewport bounds
    if (left + menuRect.width > viewportWidth - VIEWPORT_PADDING) {
      left = viewportWidth - menuRect.width - VIEWPORT_PADDING
    }
    if (left < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING
    }
    if (top + menuRect.height > viewportHeight - VIEWPORT_PADDING) {
      top = buttonRect.top - menuRect.height - MENU_GAP
    }
    if (top < VIEWPORT_PADDING) {
      top = VIEWPORT_PADDING
    }

    this.el.style.position = "fixed"
    this.el.style.top = `${top}px`
    this.el.style.left = `${left}px`
  },
}

export default PopoverMenuHook
